import { useEffect, useState, useCallback, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Image, Star, Trophy, MessageSquare, Bot, Eye, Mail, Send, ClipboardList, TrendingUp, TrendingDown, Globe, Smartphone, Monitor, Tablet } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell, Legend } from 'recharts';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { getReferrerSource } from '@/lib/userAgent';
import type { Database } from '@/integrations/supabase/types';

type PageView = Database['public']['Tables']['page_views']['Row'];

const OWNER_EMAIL = 'vesely.info@gmail.com';
const RANGES = [
  { id: '24h', label: '24h', days: 1 },
  { id: '7d', label: '7 dní', days: 7 },
  { id: '30d', label: '30 dní', days: 30 },
  { id: '90d', label: '90 dní', days: 90 },
] as const;

const PIE_COLORS = ['hsl(var(--primary))', 'hsl(var(--primary) / 0.7)', 'hsl(var(--primary) / 0.5)', 'hsl(var(--primary) / 0.35)', 'hsl(var(--muted-foreground))'];

const Dashboard = () => {
  const [range, setRange] = useState<typeof RANGES[number]['id']>('30d');
  const [stats, setStats] = useState({ gallery: 0, testimonials: 0, wallOfFame: 0, messages: 0, unreadMessages: 0, chatbotLeads: 0, activeOrders: 0 });
  const [views, setViews] = useState<PageView[]>([]);
  const [viewsPrev, setViewsPrev] = useState<PageView[]>([]);
  const [orderPhases, setOrderPhases] = useState<Record<string, number>>({});
  const [sendingTest, setSendingTest] = useState(false);

  const days = useMemo(() => RANGES.find((r) => r.id === range)!.days, [range]);

  const sendTestEmail = async () => {
    setSendingTest(true);
    try {
      const sentAt = new Date().toLocaleString('cs-CZ', { dateStyle: 'long', timeStyle: 'short' });
      const { error } = await supabase.functions.invoke('send-transactional-email', {
        body: { templateName: 'admin-test-notification', recipientEmail: OWNER_EMAIL, idempotencyKey: `test-${Date.now()}`, templateData: { sentAt } },
      });
      if (error) toast.error(`Chyba: ${error.message}`);
      else toast.success(`Test odeslán na ${OWNER_EMAIL}`);
    } catch (e) {
      toast.error('Odeslání selhalo');
    } finally {
      setSendingTest(false);
    }
  };

  const fetchAll = useCallback(async () => {
    const now = new Date();
    const start = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
    const prevStart = new Date(start.getTime() - days * 24 * 60 * 60 * 1000);

    const [g, t, w, m, u, cl, ord, vNow, vPrev] = await Promise.all([
      supabase.from('gallery_items').select('id', { count: 'exact', head: true }),
      supabase.from('testimonials').select('id', { count: 'exact', head: true }),
      supabase.from('wall_of_fame').select('id', { count: 'exact', head: true }),
      supabase.from('contact_messages').select('id', { count: 'exact', head: true }),
      supabase.from('contact_messages').select('id', { count: 'exact', head: true }).eq('is_read', false),
      supabase.from('chatbot_leads').select('id', { count: 'exact', head: true }),
      supabase.from('orders').select('phase'),
      supabase.from('page_views').select('*').gte('created_at', start.toISOString()).order('created_at'),
      supabase.from('page_views').select('*').gte('created_at', prevStart.toISOString()).lt('created_at', start.toISOString()),
    ]);

    const phasesCount: Record<string, number> = {};
    (ord.data ?? []).forEach((o) => { phasesCount[o.phase] = (phasesCount[o.phase] || 0) + 1; });
    const active = (ord.data ?? []).filter((o) => o.phase !== 'done' && o.phase !== 'cancelled').length;

    setOrderPhases(phasesCount);
    setStats({
      gallery: g.count ?? 0, testimonials: t.count ?? 0, wallOfFame: w.count ?? 0,
      messages: m.count ?? 0, unreadMessages: u.count ?? 0, chatbotLeads: cl.count ?? 0, activeOrders: active,
    });
    const LOVABLE_RE = /lovable\.(dev|app)|lovableproject\.com/i;
    const filterInternal = (rows: PageView[]) => rows.filter((r) => !(r.referrer && LOVABLE_RE.test(r.referrer)));
    setViews(filterInternal(vNow.data ?? []));
    setViewsPrev(filterInternal(vPrev.data ?? []));
  }, [days]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Derived analytics
  const totalViews = views.length;
  const uniqueSessions = new Set(views.map((v) => v.session_id).filter(Boolean)).size;
  const prevViews = viewsPrev.length;
  const trend = prevViews > 0 ? Math.round(((totalViews - prevViews) / prevViews) * 100) : null;

  // Time series
  const chartData = useMemo(() => {
    const map: Record<string, { views: number; sessions: Set<string> }> = {};
    views.forEach((v) => {
      const day = v.created_at.slice(0, 10);
      if (!map[day]) map[day] = { views: 0, sessions: new Set() };
      map[day].views++;
      if (v.session_id) map[day].sessions.add(v.session_id);
    });
    return Object.entries(map).sort(([a], [b]) => a.localeCompare(b)).map(([date, val]) => ({
      date: date.slice(5), views: val.views, unique: val.sessions.size,
    }));
  }, [views]);

  // Sources
  const sources = useMemo(() => {
    const m: Record<string, number> = {};
    views.filter((v) => v.is_landing || true).forEach((v) => {
      const s = getReferrerSource(v.referrer);
      m[s] = (m[s] || 0) + 1;
    });
    return Object.entries(m).sort(([, a], [, b]) => b - a).slice(0, 6).map(([name, value]) => ({ name, value }));
  }, [views]);

  // Geography
  const countries = useMemo(() => {
    const m: Record<string, number> = {};
    views.forEach((v) => { if (v.country) m[v.country] = (m[v.country] || 0) + 1; });
    return Object.entries(m).sort(([, a], [, b]) => b - a).slice(0, 10).map(([name, value]) => ({ name, value }));
  }, [views]);

  const cities = useMemo(() => {
    const m: Record<string, number> = {};
    views.forEach((v) => { if (v.city) m[v.city] = (m[v.city] || 0) + 1; });
    return Object.entries(m).sort(([, a], [, b]) => b - a).slice(0, 10).map(([name, value]) => ({ name, value }));
  }, [views]);

  // Devices
  const devices = useMemo(() => {
    const m: Record<string, number> = { mobile: 0, desktop: 0, tablet: 0 };
    views.forEach((v) => { if (v.device_type) m[v.device_type] = (m[v.device_type] || 0) + 1; });
    return [
      { name: 'Mobil', value: m.mobile, icon: Smartphone },
      { name: 'Desktop', value: m.desktop, icon: Monitor },
      { name: 'Tablet', value: m.tablet, icon: Tablet },
    ];
  }, [views]);

  // Browsers
  const browsers = useMemo(() => {
    const m: Record<string, number> = {};
    views.forEach((v) => { if (v.browser) m[v.browser] = (m[v.browser] || 0) + 1; });
    return Object.entries(m).sort(([, a], [, b]) => b - a).map(([name, value]) => ({ name, value }));
  }, [views]);

  // Top pages
  const topPages = useMemo(() => {
    const m: Record<string, number> = {};
    views.forEach((v) => { m[v.page_url] = (m[v.page_url] || 0) + 1; });
    return Object.entries(m).sort(([, a], [, b]) => b - a).slice(0, 8).map(([page, count]) => ({ page, count }));
  }, [views]);

  // Landing pages
  const landingPages = useMemo(() => {
    const m: Record<string, number> = {};
    views.filter((v) => v.is_landing).forEach((v) => { m[v.page_url] = (m[v.page_url] || 0) + 1; });
    return Object.entries(m).sort(([, a], [, b]) => b - a).slice(0, 6).map(([page, count]) => ({ page, count }));
  }, [views]);

  // Hourly heatmap (day-of-week × hour)
  const heatmap = useMemo(() => {
    const grid: number[][] = Array.from({ length: 7 }, () => Array(24).fill(0));
    views.forEach((v) => {
      const d = new Date(v.created_at);
      grid[d.getDay()][d.getHours()]++;
    });
    return grid;
  }, [views]);
  const heatmapMax = Math.max(1, ...heatmap.flat());

  // Funnel
  const totalSessions = uniqueSessions;
  const leadsInRange = views.length > 0 ? Math.min(stats.chatbotLeads, totalSessions) : 0;
  const messagesInRange = stats.messages > 0 ? Math.min(stats.messages, totalSessions) : 0;

  // Avg duration
  const durations = views.map((v) => v.duration_ms).filter((d): d is number => typeof d === 'number' && d > 0 && d < 30 * 60 * 1000);
  const avgDuration = durations.length ? Math.round(durations.reduce((a, b) => a + b, 0) / durations.length / 1000) : 0;

  // Bounce rate (sessions with only 1 view)
  const sessionViewCounts: Record<string, number> = {};
  views.forEach((v) => { if (v.session_id) sessionViewCounts[v.session_id] = (sessionViewCounts[v.session_id] || 0) + 1; });
  const bouncedSessions = Object.values(sessionViewCounts).filter((c) => c === 1).length;
  const bounceRate = uniqueSessions > 0 ? Math.round((bouncedSessions / uniqueSessions) * 100) : 0;

  // UTM
  const utmSources = useMemo(() => {
    const m: Record<string, number> = {};
    views.forEach((v) => { if (v.utm_source) m[v.utm_source] = (m[v.utm_source] || 0) + 1; });
    return Object.entries(m).sort(([, a], [, b]) => b - a).slice(0, 6).map(([name, value]) => ({ name, value }));
  }, [views]);

  const cards = [
    { label: 'Aktivní zakázky', value: stats.activeOrders, icon: ClipboardList },
    { label: 'Fotky v galerii', value: stats.gallery, icon: Image },
    { label: 'Reference', value: stats.testimonials, icon: Star },
    { label: 'Wall of Fame', value: stats.wallOfFame, icon: Trophy },
    { label: 'Zprávy', value: `${stats.unreadMessages} / ${stats.messages}`, icon: MessageSquare },
    { label: 'Chatbot leady', value: stats.chatbotLeads, icon: Bot },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="text-4xl tracking-wider text-foreground" style={{ fontFamily: 'Bebas Neue' }}>
          Dashboard
        </h1>
        <div className="flex gap-1 p-1 bg-card border border-border rounded-md">
          {RANGES.map((r) => (
            <button
              key={r.id}
              onClick={() => setRange(r.id)}
              className={`px-3 py-1 text-xs rounded transition-colors ${range === r.id ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        {cards.map((card) => (
          <div key={card.label} className="p-4 bg-card border border-border rounded-md">
            <card.icon className="text-primary mb-2" size={20} />
            <p className="text-xl font-bold text-foreground">{card.value}</p>
            <p className="text-xs text-muted-foreground">{card.label}</p>
          </div>
        ))}
      </div>

      {/* Email notifications card */}
      <div className="p-5 bg-card border border-border rounded-md mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-start gap-3">
          <Mail className="text-primary mt-1 shrink-0" size={20} />
          <div>
            <h2 className="text-base font-semibold text-foreground">Emailové notifikace</h2>
            <p className="text-sm text-muted-foreground">
              Chodí na <strong className="text-foreground">{OWNER_EMAIL}</strong> při nové zprávě nebo leadu.
            </p>
          </div>
        </div>
        <Button onClick={sendTestEmail} disabled={sendingTest} size="sm">
          <Send size={14} className="mr-2" />
          {sendingTest ? 'Odesílám…' : 'Poslat test'}
        </Button>
      </div>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="overview">Přehled</TabsTrigger>
          <TabsTrigger value="sources">Zdroje</TabsTrigger>
          <TabsTrigger value="geo">Geografie</TabsTrigger>
          <TabsTrigger value="devices">Zařízení</TabsTrigger>
          <TabsTrigger value="behavior">Chování</TabsTrigger>
        </TabsList>

        {/* OVERVIEW */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiCard icon={Eye} label="Zobrazení" value={totalViews} trend={trend} />
            <KpiCard icon={Globe} label="Unikátní" value={uniqueSessions} />
            <KpiCard label="Prům. čas" value={avgDuration > 0 ? `${avgDuration}s` : '–'} />
            <KpiCard label="Bounce rate" value={`${bounceRate}%`} />
          </div>

          <Panel title="Návštěvnost v čase">
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} />
                  <YAxis tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} />
                  <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 6, color: 'hsl(var(--foreground))' }} />
                  <Bar dataKey="views" name="Zobrazení" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="unique" name="Unikátní" fill="hsl(var(--primary) / 0.4)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <EmptyMsg />}
          </Panel>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Panel title="Konverzní funnel">
              <FunnelRow label="Návštěvy (unique)" value={totalSessions} max={totalSessions} />
              <FunnelRow label="Zprávy z formuláře (celkem)" value={messagesInRange} max={totalSessions} />
              <FunnelRow label="Chatbot leady (celkem)" value={leadsInRange} max={totalSessions} />
              <p className="text-xs text-muted-foreground mt-3">Konverzní poměr: {totalSessions > 0 ? Math.round(((messagesInRange + leadsInRange) / totalSessions) * 100) : 0}%</p>
            </Panel>

            <Panel title="Zakázky podle fáze">
              <div className="space-y-2">
                {['inquiry', 'design', 'airbrush', 'clear_coat', 'done'].map((p) => (
                  <div key={p} className="flex justify-between text-sm">
                    <span className="text-muted-foreground capitalize">{p.replace('_', ' ')}</span>
                    <span className="font-medium text-foreground">{orderPhases[p] ?? 0}</span>
                  </div>
                ))}
              </div>
            </Panel>
          </div>
        </TabsContent>

        {/* SOURCES */}
        <TabsContent value="sources" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Panel title="Zdroje návštěvnosti">
              {sources.length > 0 ? (
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie data={sources} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={(e) => e.name}>
                      {sources.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 6 }} />
                  </PieChart>
                </ResponsiveContainer>
              ) : <EmptyMsg />}
            </Panel>

            <Panel title="UTM kampaně">
              {utmSources.length > 0 ? (
                <RankList items={utmSources.map((u) => ({ label: u.name, count: u.value }))} />
              ) : <p className="text-sm text-muted-foreground">Zatím žádné UTM označené návštěvy. Použij např. <code className="bg-secondary px-1 rounded">?utm_source=facebook&utm_campaign=jaro2026</code> v odkazech.</p>}
            </Panel>
          </div>
        </TabsContent>

        {/* GEOGRAPHY */}
        <TabsContent value="geo" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Panel title="Top země">
              {countries.length > 0
                ? <RankList items={countries.map((c) => ({ label: c.name, count: c.value }))} />
                : <EmptyMsg />}
            </Panel>
            <Panel title="Top města">
              {cities.length > 0
                ? <RankList items={cities.map((c) => ({ label: c.name, count: c.value }))} />
                : <EmptyMsg />}
            </Panel>
          </div>
        </TabsContent>

        {/* DEVICES */}
        <TabsContent value="devices" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {devices.map((d) => (
              <div key={d.name} className="p-5 bg-card border border-border rounded-md text-center">
                <d.icon className="text-primary mx-auto mb-2" size={28} />
                <p className="text-3xl font-bold text-foreground">{d.value}</p>
                <p className="text-sm text-muted-foreground">{d.name}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {totalViews > 0 ? Math.round((d.value / totalViews) * 100) : 0}%
                </p>
              </div>
            ))}
          </div>
          <Panel title="Prohlížeče">
            {browsers.length > 0 ? (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={browsers}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} />
                  <YAxis tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} />
                  <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 6 }} />
                  <Bar dataKey="value" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <EmptyMsg />}
          </Panel>
        </TabsContent>

        {/* BEHAVIOR */}
        <TabsContent value="behavior" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Panel title="Top stránky">
              <RankList items={topPages.map((p) => ({ label: p.page, count: p.count }))} />
            </Panel>
            <Panel title="Vstupní stránky">
              {landingPages.length > 0
                ? <RankList items={landingPages.map((p) => ({ label: p.page, count: p.count }))} />
                : <EmptyMsg />}
            </Panel>
          </div>

          <Panel title="Kdy chodí návštěvníci (den × hodina)">
            <div className="overflow-x-auto">
              <div className="inline-block">
                <div className="flex gap-1 text-[10px] text-muted-foreground pl-8">
                  {Array.from({ length: 24 }).map((_, h) => (
                    <div key={h} className="w-5 text-center">{h}</div>
                  ))}
                </div>
                {['Ne', 'Po', 'Út', 'St', 'Čt', 'Pá', 'So'].map((day, i) => (
                  <div key={day} className="flex gap-1 items-center mt-1">
                    <div className="w-6 text-[10px] text-muted-foreground">{day}</div>
                    {heatmap[i].map((val, h) => {
                      const intensity = val / heatmapMax;
                      return (
                        <div
                          key={h}
                          className="w-5 h-5 rounded-sm"
                          style={{ background: val === 0 ? 'hsl(var(--secondary))' : `hsl(var(--primary) / ${0.15 + intensity * 0.85})` }}
                          title={`${day} ${h}:00 — ${val} zobrazení`}
                        />
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          </Panel>
        </TabsContent>
      </Tabs>
    </div>
  );
};

// --- helpers ---

const KpiCard = ({ icon: Icon, label, value, trend }: { icon?: typeof Eye; label: string; value: number | string; trend?: number | null }) => (
  <div className="p-4 bg-card border border-border rounded-md">
    <div className="flex items-center justify-between mb-1">
      <span className="text-xs text-muted-foreground">{label}</span>
      {Icon && <Icon size={14} className="text-muted-foreground" />}
    </div>
    <p className="text-2xl font-bold text-foreground">{value}</p>
    {typeof trend === 'number' && (
      <div className={`flex items-center gap-1 text-xs mt-1 ${trend >= 0 ? 'text-emerald-500' : 'text-destructive'}`}>
        {trend >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
        {Math.abs(trend)}% vs předchozí období
      </div>
    )}
  </div>
);

const Panel = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div className="p-5 bg-card border border-border rounded-md">
    <h2 className="text-base font-semibold text-foreground mb-3">{title}</h2>
    {children}
  </div>
);

const EmptyMsg = () => <p className="text-sm text-muted-foreground text-center py-8">Zatím žádná data.</p>;

const RankList = ({ items }: { items: { label: string; count: number }[] }) => {
  const max = Math.max(1, ...items.map((i) => i.count));
  return (
    <div className="space-y-2">
      {items.map((it, i) => (
        <div key={it.label}>
          <div className="flex justify-between text-sm mb-0.5">
            <span className="text-foreground truncate"><span className="text-muted-foreground mr-2">{i + 1}.</span>{it.label}</span>
            <span className="text-primary font-medium ml-2">{it.count}</span>
          </div>
          <div className="h-1 bg-secondary rounded-full overflow-hidden">
            <div className="h-full bg-primary" style={{ width: `${(it.count / max) * 100}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
};

const FunnelRow = ({ label, value, max }: { label: string; value: number; max: number }) => {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div className="mb-3">
      <div className="flex justify-between text-sm mb-1">
        <span className="text-foreground">{label}</span>
        <span className="text-primary font-medium">{value} ({Math.round(pct)}%)</span>
      </div>
      <div className="h-2 bg-secondary rounded-full overflow-hidden">
        <div className="h-full bg-primary" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

export default Dashboard;
